/**
 * HttpError utility for handling HTTP responses and creating standardized errors
 */

export interface HttpErrorOptions {
  statusCode: number
  message?: string
  originalResponse?: Response
}

export class HttpError extends Error {
  public readonly statusCode: number
  public readonly originalResponse?: Response

  constructor(options: HttpErrorOptions) {
    super(options.message || `HTTP ${options.statusCode} Error`)
    this.name = 'HttpError'
    this.statusCode = options.statusCode
    this.originalResponse = options.originalResponse
  }
}

/**
 * Parses a fetch Response and creates an HttpError with the server's error message
 */
export async function createHttpErrorFromResponse(response: Response): Promise<HttpError> {
  let errorMessage = `HTTP ${response.status} Error`
  const cloned = response.clone()

  try {
    const errorData = await cloned.json()
    errorMessage = errorData.message || errorData.error || errorData.detail || errorMessage
  } catch {
    // If we can't parse the response, use the status text
    errorMessage = response.statusText || errorMessage
  }

  return new HttpError({
    statusCode: response.status,
    message: errorMessage,
    originalResponse: response,
  })
}

/**
 * Checks if a fetch response is ok, and throws an HttpError if not
 */
export async function throwIfNotOk(response: Response): Promise<Response> {
  if (!response.ok) {
    throw await createHttpErrorFromResponse(response)
  }
  return response
}
